import {
  ADDRESS_NOT_SHARED_VALUE,
  formValue,
  whenAll,
  whenNot,
} from 'platform/shared/health-apps';
import { isNewSubmission } from './applicationStatus';

const NEW_SUBMISSION_NAME_PATHS = {
  other: 'certifierName',
  sponsor: 'sponsorName',
};

const EXISTING_SUBMISSION_NAME_PATHS = {
  other: 'contactName',
  sponsor: 'sponsorName',
};

const getApplicantNamePath = formData => {
  if (!isNewSubmission(formData)) return 'applicantName';
  const appIndex = formData['view:certifierApplicantIndex'] ?? 0;
  return `applicants.${appIndex}.applicantName`;
};

const certifierRoleIs = role => formData =>
  formValue('certifierRole')(formData) === role;

export const roleIsApplicant = certifierRoleIs('applicant');
export const roleIsOther = certifierRoleIs('other');

export const roleIsSponsor = certifierRoleIs('sponsor');
export const roleIsNotSponsor = whenNot(roleIsSponsor);

const certifierAddressIsNotShared = formData => {
  const sharedWith = formValue('view:certifierSharedAddress')(formData);
  return !sharedWith || sharedWith === ADDRESS_NOT_SHARED_VALUE;
};

export const certifierHasNoSharedAddressSelection = whenAll(
  roleIsOther,
  certifierAddressIsNotShared,
);

/**
 * Returns the dot-notation form data path for the certifier's name.
 *
 * New submissions store applicant certifier names under the selected applicant's
 * nested `applicantName` path. Existing/update submissions use top-level name
 * fields (for example `contactName` for `other`).
 *
 * @param {Object} [formData={}] - The current form data.
 * @returns {string} Dot-notation path to the certifier's name field.
 *
 * @example
 * getCertifierNamePath({ certifierRole: 'other' });
 * // => 'certifierName'
 *
 * @example
 * getCertifierNamePath({ certifierRole: 'sponsor' });
 * // => 'sponsorName'
 *
 * @example
 * getCertifierNamePath({
 *   certifierRole: 'applicant',
 *   'view:certifierApplicantIndex': 1,
 * });
 * // => 'applicants.1.applicantName'
 *
 * @example
 * getCertifierNamePath({
 *   certifierRole: 'other',
 *   submissionType: 'existing',
 * });
 * // => 'contactName'
 *
 */
export const getCertifierNamePath = (formData = {}) => {
  const namePaths = isNewSubmission(formData)
    ? NEW_SUBMISSION_NAME_PATHS
    : EXISTING_SUBMISSION_NAME_PATHS;
  return namePaths[formData.certifierRole] ?? getApplicantNamePath(formData);
};
